import { useEffect, useState } from "react";
import type { SubscriberOverridePrefill } from "@/lib/salesOrgPricingClient";

type OverrideRow = {
  id: string;
  product_line: string;
  width_ft: number;
  length_ft: number;
  package_key: string | null;
  options_key: string | null;
  override_msrp: number;
  currency: string | null;
};

export function OrgSettingsSectionPricingOverrides(props: {
  busy: boolean;
  overrides: unknown[];
  show: boolean;
  /** When revision changes, form fields are filled from the manufacturer row pick. */
  prefill: SubscriberOverridePrefill | null;
  onUpsert: (payload: Record<string, unknown>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const { busy, overrides, show, prefill, onUpsert, onDelete } = props;
  const [product_line, setProductLine] = useState("");
  const [width_ft, setWidthFt] = useState("");
  const [length_ft, setLengthFt] = useState("");
  const [package_key, setPackageKey] = useState("");
  const [options_key, setOptionsKey] = useState("");
  const [override_msrp, setOverrideMsrp] = useState("");

  useEffect(() => {
    if (!prefill) return;
    setProductLine(prefill.product_line);
    setWidthFt(String(prefill.width_ft));
    setLengthFt(String(prefill.length_ft));
    setPackageKey(prefill.package_key ?? "");
    setOptionsKey(prefill.options_key ?? "");
    setOverrideMsrp(String(prefill.base_msrp));
  }, [prefill?.revision, prefill]);

  if (!show) return null;

  const rows = (Array.isArray(overrides) ? overrides : []) as OverrideRow[];

  return (
    <div className="space-y-2 border-t border-gray-200 dark:border-gray-700 pt-3">
      <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">Subscriber MSRP overrides</div>
      <p className="text-[11px] text-gray-500">
        Overrides replace the manufacturer MSRP for the same product line, width, length, and optional package/options keys (see manufacturer catalog or snapshot
        table).
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <input
          className="text-xs border rounded px-2 py-1 bg-white dark:bg-gray-900 dark:border-gray-700"
          placeholder="product_line"
          value={product_line}
          disabled={busy}
          onChange={(e) => setProductLine(e.target.value)}
        />
        <input
          className="text-xs border rounded px-2 py-1 bg-white dark:bg-gray-900 dark:border-gray-700"
          placeholder="width_ft"
          value={width_ft}
          disabled={busy}
          onChange={(e) => setWidthFt(e.target.value)}
        />
        <input
          className="text-xs border rounded px-2 py-1 bg-white dark:bg-gray-900 dark:border-gray-700"
          placeholder="length_ft"
          value={length_ft}
          disabled={busy}
          onChange={(e) => setLengthFt(e.target.value)}
        />
        <input
          className="text-xs border rounded px-2 py-1 bg-white dark:bg-gray-900 dark:border-gray-700"
          placeholder="package_key (opt)"
          value={package_key}
          disabled={busy}
          onChange={(e) => setPackageKey(e.target.value)}
        />
        <input
          className="text-xs border rounded px-2 py-1 bg-white dark:bg-gray-900 dark:border-gray-700"
          placeholder="options_key (opt)"
          value={options_key}
          disabled={busy}
          onChange={(e) => setOptionsKey(e.target.value)}
        />
        <input
          className="text-xs border rounded px-2 py-1 bg-white dark:bg-gray-900 dark:border-gray-700"
          placeholder="override_msrp"
          value={override_msrp}
          disabled={busy}
          onChange={(e) => setOverrideMsrp(e.target.value)}
        />
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() =>
          void onUpsert({
            product_line,
            width_ft: Number(width_ft),
            length_ft: Number(length_ft),
            package_key: package_key.trim() || null,
            options_key: options_key.trim() || null,
            override_msrp: Number(override_msrp),
          })
        }
        className="px-3 py-1.5 rounded-lg border border-indigo-300 text-indigo-900 text-sm bg-indigo-50"
      >
        Save override
      </button>
      {rows.length ? (
        <ul className="text-xs space-y-1 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded p-2">
          {rows.map((r) => (
            <li key={r.id} className="flex justify-between gap-2 items-center">
              <span className="font-mono truncate">
                {r.product_line} {r.width_ft}x{r.length_ft} → {r.override_msrp} {r.currency || "USD"}
              </span>
              <button
                type="button"
                disabled={busy}
                className="shrink-0 text-red-600 hover:underline"
                onClick={() => void onDelete(r.id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-500">No overrides yet.</p>
      )}
    </div>
  );
}
